<script>
    import { onMount } from "svelte";
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
    import { loadToolsScript } from "$lib/utils/scripts";

    onMount(async () => {
        await loadToolsScript();
        if (typeof window !== "undefined" && window.initToolsOrbit) {
            window.initToolsOrbit();
        }
    });
</script>

<svelte:head>
    <title
        >dinglandia | Web Design & Development, Graphic Design & Branding</title
    >
    <meta
        name="description"
        content="We are a unique web design/development company that considers performance and optimization to be at the forefront of importance for SEO."
    />
    <meta
        name="keywords"
        content="web design, web development, graphic design, progressive web applications, PWAs, UI, UX, user interface design, user experience design, performance, optimization, automation, content strategy, marketing, SEO, SEM, SMM, search engine optimization, search engine marketing, social media marketing"
    />

    <meta
        property="og:title"
        content="Web Design & Development, Graphic Design & Branding"
    />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/" />
    <meta
        property="og:description"
        content="We are a unique web design/development company that considers performance and optimization to be at the forefront of importance for SEO."
    />
    <meta property="og:type" content="business.business" />
    <meta property="og:image" content="https://dinglandia.design/og/home.jpg" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@dinglestein" />
    <meta
        name="twitter:title"
        content="dinglandia | Web Design & Development, Graphic Design & Branding"
    />
    <meta
        name="twitter:description"
        content="We are a unique web design/development company that considers performance and optimization to be at the forefront of importance for SEO."
    />
    <meta
        name="twitter:image"
        content="https://dinglandia.design/og/home.jpg"
    />
</svelte:head>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new" name="logo"></div>
    <h1 class="flash">dinglandia</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#tools" aria-label="Jump to Future Proofed Builds">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Tools Showcase -->
<section id="tools" class="tools-cta" aria-labelledby="tools-cta-title">
    <div class="section-wrapper">
        <header class="tools-cta--header">
            <hgroup class="hgroup">
                <h2 id="tools-cta-title" class="headline">
                    Future Proofed Builds
                </h2>
                <p class="tagline">
                    build a successful tech stack with performance + UX goals at
                    the forefront of design &amp; development
                </p>
            </hgroup>
            <!-- <a href="/contact" role="button" class="cta-btn"
        data-fillout-id="n2vCxyr9Huus"
        data-fillout-embed-type="popup"
        data-fillout-dynamic-resize
        data-fillout-inherit-parameters
        data-fillout-popup-size="small"
      >getter done</a> -->
        </header>
        <ul class="tools-cta--cards">
            <li class="tools-cta--card">
                <a
                    href="https://qwik.dev/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-qwik_c.svg"
                            width="144"
                            height="144"
                            alt="Qwik Javascript Framework"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Qwik</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">JS Framework</p>
                            <p class="team-bio">
                                Deliver instant apps at scale.
                            </p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://www.builder.io/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-builder_c.svg"
                            width="144"
                            height="144"
                            alt="Builder.io content management system"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Builder</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">CMS</p>
                            <p class="team-bio">
                                Take Ideas to Production in Seconds, Not
                                Sprints.
                            </p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://mitosis.builder.io/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-mitosis_c.svg"
                            width="144"
                            height="144"
                            alt="Mitosis component builder"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Mitosis</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Components</p>
                            <p class="team-bio">
                                Write components once, run everywhere.
                            </p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://www.figma.com/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-figma_c.svg"
                            width="144"
                            height="144"
                            alt="Figma Design Tool"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Figma</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Design Tool</p>
                            <p class="team-bio">Think bigger. Build faster.</p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://partytown.qwik.dev/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-partytown_c.svg"
                            width="144"
                            height="144"
                            alt="Partytown JS service worker"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Partytown</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Service Worker</p>
                            <p class="team-bio">
                                Run third-party scripts from a web worker.
                            </p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://developer.mozilla.org/en-US/docs/Web/CSS"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-css_c.svg"
                            width="144"
                            height="144"
                            alt="CSS: Cascading Style Sheets"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">CSS</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">The Good Stuff</p>
                            <p class="team-bio">CSS: Cascading Style Sheets</p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://vite.dev/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-vite_c.svg"
                            width="144"
                            height="144"
                            alt="Vite build tool"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Vite</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Build Tool</p>
                            <p class="team-bio">
                                Blazing fast frontend build tool.
                            </p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://www.netlify.com/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-netlify_c.svg"
                            width="144"
                            height="144"
                            alt="Netlify cloud hosting"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Netlify</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Cloud Hosting</p>
                            <p class="team-bio">Push your ideas to the web.</p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://www.cloudflare.com/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-cloudflare_c.svg"
                            width="144"
                            height="144"
                            alt="Cloudflare security"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Cloudflare</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Cloud Security</p>
                            <p class="team-bio">Connect, protect, and build.</p>
                        </div>
                    </div>
                </a>
            </li>
            <li class="tools-cta--card">
                <a
                    href="https://www.notion.com/"
                    class="avatar-link-wrapper"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <div class="visual">
                        <img
                            class="avatar-img"
                            src="/img/icons/dinglandia--icon-notion_c.svg"
                            width="144"
                            height="144"
                            alt="Notion workspace"
                        />
                    </div>
                    <div class="tooltiptext">
                        <h3 class="team-name">Notion</h3>
                        <div class="team-content-wrapper">
                            <p class="team-title">Cloud Workspace</p>
                            <p class="team-bio">Write. Plan. Collaborate.</p>
                        </div>
                    </div>
                </a>
            </li>
        </ul>
        <svg style="display: none">
            <filter id="lg-dist" x="0%" y="0%" width="100%" height="100%">
                <feTurbulence
                    type="fractalNoise"
                    baseFrequency="0.008 0.008"
                    numOctaves="2"
                    seed="92"
                    result="noise"
                />
                <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
                <feDisplacementMap
                    in="SourceGraphic"
                    in2="blurred"
                    scale="70"
                    xChannelSelector="R"
                    yChannelSelector="G"
                />
            </filter>
        </svg>
    </div>
</section>
<!-- //Tools Showcase -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout" style="position: relative">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img src="/img/mountains/mtnBlue.svg" alt="" style="--offset:0.012" />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- //New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>
        <div class="services-grid">
            <a href="/services#v-pills-discover">
                <svg class="icon icon-discover"
                    ><use xlink:href="#icon-discover"></use></svg
                >
                <p>Discover</p>
            </a>
            <a href="/services#v-pills-design">
                <svg class="icon icon-design"
                    ><use xlink:href="#icon-design"></use></svg
                >
                <p>Design</p>
            </a>
            <a href="/services#v-pills-develop">
                <svg class="icon icon-develop"
                    ><use xlink:href="#icon-develop"></use></svg
                >
                <p>Develop</p>
            </a>
            <a href="/services#v-pills-devops">
                <svg class="icon icon-devops"
                    ><use xlink:href="#icon-devops"></use></svg
                >
                <p>DevOps</p>
            </a>
            <a href="/services#v-pills-agentic">
                <svg class="icon icon-robot"
                    ><use xlink:href="#icon-robot"></use></svg
                >
                <p>Agentic</p>
            </a>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a href="/work" class="btn btn-lg btn-custom" type="button"
                        >view work</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    tabindex="-1"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>
<!-- Water Ripple CTA -->
